import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { i18nLanguage } from "i18n";
import { useTripperTrack } from "../context/TripperTrackingContext";
import { useTripperData } from "../hooks/useTripperData";
import { useRichPreviewAutoProgress } from "../hooks/useRichPreviewAutoProgress";
import { getCloudinaryImageUrl } from "../utils/getCloudinaryImageUrl";
import { getCoverMedia } from "../utils/getCoverMedia";
import { getLocalizedString } from "../utils/getLocalizedString";
import {
  flattenStopBlocks,
  getStopsWithCoordinates,
  getKmDistanceBetweenCoordinates,
} from "../utils/mapHelpers";
import { normalizeMapStyleVariant } from "../../../apps/tripper/components/mapStyleTypes";
import { GroupBlock, StopBlock, OperatorRecord, SegmentData } from "../types";
import { TripperModal } from "./TripperModal";
import { RichLauncherMosaic } from "./RichLauncherMosaic";
// Imported statically (not via React.lazy). Historically, splitting this
// component into its own chunk caused duplicate-React issues when the embed
// script was loaded via a cross-origin 302 redirect (see specs/). The embed
// now loads directly from the CDN with crossorigin="anonymous", so splitting
// is safe in principle — but unless there's a real first-paint win to chase,
// keep it inlined: embed code-splitting is a sharp tool and this component
// renders on virtually every rich-launcher mount.
import RichLauncherMap, { type RichLauncherMapHandle } from "./RichLauncherMap";

type TripperRichPreviewProps = {
  itineraryId: string;
  language: i18nLanguage;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

type Coordinates = [number, number];

type ResolvedStop = {
  stop: StopBlock;
  operator: NonNullable<OperatorRecord> | null;
  coordinates: Coordinates | null;
  imageUrl: string | null;
  name: string;
  address: string;
  businessType: string | null;
  markerNumber: number;
};

function resolveStops(
  segments: SegmentData[],
  operatorByEntityId: Record<string, NonNullable<OperatorRecord>>,
  language: i18nLanguage,
): ResolvedStop[] {
  const allStops = flattenStopBlocks(segments);
  const withCoords = getStopsWithCoordinates(segments, operatorByEntityId);
  const coordsMap = new Map(withCoords.map((s) => [s.item._id, s.coordinates]));

  const operatorStops = allStops.filter((stop) => {
    const entityId = stop.entityRef?.entityId?.toString();
    return entityId && operatorByEntityId[entityId];
  });

  return operatorStops.map((stop, i) => {
    const entityId = stop.entityRef!.entityId!.toString();
    const operator = operatorByEntityId[entityId];
    const coordinates = coordsMap.get(stop._id) ?? null;

    const name = operator.name
      ? getLocalizedString(operator.name, language)
      : "Stop";

    const addr = operator?.contact?.address;
    const addressParts = [addr?.line1, addr?.city, addr?.subdivision].filter(
      Boolean,
    );
    const address = addressParts.join(", ");

    const imageId = stop.featuredImageId ?? operator?.images?.[0]?._id ?? null;
    const imageUrl = getCloudinaryImageUrl(imageId, "f_auto,w_760,c_limit");

    const rawType = operator?.type ?? null;
    const businessType = rawType
      ? rawType
          .replace(/_/g, " ")
          .replace(/business type/i, "")
          .trim()
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : null;

    return {
      stop,
      operator,
      coordinates,
      imageUrl,
      name,
      address,
      businessType,
      markerNumber: i + 1,
    };
  });
}

function flyDuration(distKm: number, isFirst: boolean): number {
  if (isFirst) return 2000;
  if (distKm < 2) return 1500;
  if (distKm < 20) return 1500 + (distKm / 20) * 1500;
  return 3000 + Math.min(distKm / 80, 1) * 1500;
}

// ── Silent error boundary (renders nothing on failure) ───────────────────────

class SilentErrorBoundary extends Component<
  { children: ReactNode; label?: string; onError?: () => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(`[RichLauncher] ${this.props.label ?? "child"} error:`, error, info);
    this.props.onError?.();
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export const TripperRichPreview = ({
  itineraryId,
  language,
}: TripperRichPreviewProps) => {
  const { t } = useTranslation("tripper", { keyPrefix: "EMBED" });
  const { data, isLoading, error } = useTripperData(itineraryId);
  const [modalOpen, setModalOpen] = useState(false);
  const trackPostHogEvent = useTripperTrack();
  const [mapReady, setMapReady] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);
  const mapHandleRef = useRef<RichLauncherMapHandle | null>(null);
  const prevCoordsRef = useRef<Coordinates | null>(null);

  // isMobile (< 760px) gates which map instance renders (inline vs right panel).
  // We treat either a narrow launcher container OR a narrow viewport as mobile.
  // The mosaic→hero swap at intermediate widths is handled by CSS container query.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const MOBILE_BREAKPOINT = 760;
    const el = containerRef.current;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const updateIsMobile = (containerWidth?: number) => {
      const measuredWidth = containerWidth ?? el?.getBoundingClientRect().width ?? Infinity;
      const isNarrowContainer = measuredWidth < MOBILE_BREAKPOINT;
      const isNarrowViewport = mql.matches;
      setIsMobile(isNarrowContainer || isNarrowViewport);
    };

    let observer: ResizeObserver | null = null;
    if (el) {
      observer = new ResizeObserver(([entry]) => updateIsMobile(entry.contentRect.width));
      observer.observe(el);
    }

    const handleViewportChange = () => updateIsMobile();
    mql.addEventListener("change", handleViewportChange);
    updateIsMobile();

    return () => {
      observer?.disconnect();
      mql.removeEventListener("change", handleViewportChange);
    };
  }, []);

  // Defer map mount to allow iframe/Shadow DOM to stabilise and
  // avoid focus()-related side-effects from browser extensions.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const tid = setTimeout(() => setMapMounted(true), 500);
      return () => clearTimeout(tid);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackPostHogEvent("widget_impression");
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackPostHogEvent]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const itinerary = data.itinerary;

  const previewMeta = useMemo(() => {
    if (!itinerary) return null;
    const name = getLocalizedString(itinerary.name, language);
    const orgName = itinerary.organization?.name
      ? getLocalizedString(itinerary.organization.name, language)
      : null;
    const coverMedia = getCoverMedia(
      itinerary.coverImage,
      "f_auto,w_760,c_limit",
    );
    const heroImageUrl =
      coverMedia?.kind === "mux-video"
        ? coverMedia.posterUrl
        : coverMedia?.kind === "image"
          ? coverMedia.url
          : null;

    let stopCount = 0;
    for (const seg of itinerary.segments) {
      for (const block of seg.items) {
        if (block.blockType === "STOP") stopCount++;
        if (block.blockType === "GROUP") {
          stopCount += (block as GroupBlock).items.filter(
            (b) => b.blockType === "STOP",
          ).length;
        }
      }
    }

    const distanceKm = itinerary.routeDistance
      ? Math.round(itinerary.routeDistance / 1000)
      : null;
    const isDayTrip = itinerary.tripperSettings?.segmentPreset === "DAY_TRIP";
    const segmentCount = itinerary.segments.filter(
      (s) => s.items.length > 0,
    ).length;

    return {
      name,
      orgName,
      heroImageUrl,
      stopCount,
      distanceKm,
      isDayTrip,
      segmentCount,
      travelType: itinerary.travelType,
    };
  }, [itinerary, language]);

  const resolvedStops = useMemo(
    () => resolveStops(data.segments, data.operatorByEntityId, language),
    [data.segments, data.operatorByEntityId, language],
  );

  const stopsWithCoords = useMemo(
    () => resolvedStops.filter((s) => s.coordinates !== null),
    [resolvedStops],
  );

  // Collect one image URL per stop (deduplicated) for the overview mosaic.
  const mosaicImageUrls = useMemo(() => {
    const seen = new Set<string>();
    const urls: string[] = [];
    for (const rs of resolvedStops) {
      if (rs.imageUrl && !seen.has(rs.imageUrl)) {
        seen.add(rs.imageUrl);
        urls.push(rs.imageUrl);
      }
    }
    return urls;
  }, [resolvedStops]);

  // Compute center from first coord (avoids importing mapboxgl at module scope)
  const boundsCenter = useMemo(() => {
    if (stopsWithCoords.length === 0) return null;
    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    for (const s of stopsWithCoords) {
      const [lng, lat] = s.coordinates!;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    return { lng: (minLng + maxLng) / 2, lat: (minLat + maxLat) / 2 };
  }, [stopsWithCoords]);

  // ── Auto-progress ─────────────────────────────────────────────────────────

  const { activeIndex, pause, resume } =
    useRichPreviewAutoProgress(resolvedStops.length);

  const isOverview = activeIndex === 0;
  const activeStop = isOverview ? null : resolvedStops[activeIndex - 1] ?? null;

  // ── Map settings ──────────────────────────────────────────────────────────

  const mapTheme = normalizeMapStyleVariant(
    itinerary?.tripperSettings?.mapStyleVariant,
  );
  const map3dObjects = itinerary?.tripperSettings?.map3dObjects ?? true;
  const map3dBuildings = itinerary?.tripperSettings?.map3dBuildings ?? true;
  const map3dTrees = itinerary?.tripperSettings?.map3dTrees ?? true;
  const showPoi = itinerary?.tripperSettings?.showPointsOfInterest ?? false;
  const accentColor = itinerary?.tripperSettings?.primaryColor ?? "#66800B";

  // ── Map camera ────────────────────────────────────────────────────────────

  const handleMapReady = useCallback(() => setMapReady(true), []);

  useEffect(() => {
    const handle = mapHandleRef.current;
    if (!handle?.isReady() || !mapReady) return;

    if (isOverview) {
      if (boundsCenter && stopsWithCoords.length > 0) {
        // Build a simple bounding box from the coord extremes
        const lngs = stopsWithCoords.map((s) => s.coordinates![0]);
        const lats = stopsWithCoords.map((s) => s.coordinates![1]);
        const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)];
        const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)];
        handle.fitBounds([sw, ne], {
          padding: 40,
          duration: prevCoordsRef.current ? 1200 : 0,
          pitch: 0,
          bearing: 0,
        });
      }
      prevCoordsRef.current = null;
      return;
    }

    if (!activeStop?.coordinates) return;
    const coords = activeStop.coordinates;

    const distKm = prevCoordsRef.current
      ? getKmDistanceBetweenCoordinates(prevCoordsRef.current, coords)
      : 0;
    const isFirst = !prevCoordsRef.current;
    const bearing = ((activeStop.markerNumber % 5) - 2) * 12;

    handle.flyTo({
      center: coords,
      zoom: 15.5,
      pitch: 50,
      bearing,
      duration: flyDuration(distKm, isFirst),
      essential: true,
      curve: 1.4,
    });

    prevCoordsRef.current = coords;
  }, [activeIndex, activeStop, boundsCenter, isOverview, mapReady, stopsWithCoords]);

  // ── Frosted chip (travel type on overview → itinerary name on stops) ─────

  const itineraryName = previewMeta?.name ?? null;

  const travelTypeLabel = t(
    `TRAVEL_TYPE.${previewMeta?.travelType ?? "WALKING"}`,
    { defaultValue: "Walking" },
  );
  const chipText = isOverview ? travelTypeLabel : itineraryName;

  // Derive a mid-tone background color from the active stop's image
  const [chipColor, setChipColor] = useState<string | null>(null);
  const colorCacheRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const url = activeStop?.imageUrl ?? (previewMeta?.heroImageUrl || null);
    if (!url) { setChipColor(null); return; }
    if (colorCacheRef.current[url]) { setChipColor(colorCacheRef.current[url]); return; }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 8;
        canvas.height = 8;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 8, 8);
        const { data } = ctx.getImageData(0, 0, 8, 8);
        let r = 0, g = 0, b = 0;
        const count = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i]; g += data[i + 1]; b += data[i + 2];
        }
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        r = Math.round(r * 0.6 + 128 * 0.4);
        g = Math.round(g * 0.6 + 128 * 0.4);
        b = Math.round(b * 0.6 + 128 * 0.4);
        const color = `rgba(${r}, ${g}, ${b}, 0.55)`;
        colorCacheRef.current[url] = color;
        setChipColor(color);
      } catch { /* CORS or canvas tainted — fall back to default */ }
    };
    img.src = url;
  }, [activeStop?.imageUrl, previewMeta?.heroImageUrl]);

  const nameChip = chipText ? (
    <div
      key={chipText}
      className="tripper-rich-launcher__name-chip tripper-rich-launcher__name-chip--visible"
      style={chipColor ? { "--chip-bg": chipColor } as React.CSSProperties : undefined}
    >
      <span className="tripper-rich-launcher__name-chip-text" aria-label={chipText}>
        <SlotText text={chipText} />
      </span>
    </div>
  ) : null;

  // ── Loading / error ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        className="tripper-rich-launcher__loading"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="tripper-rich-preview__spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tripper-rich-preview__message" role="alert">
        {t("LOAD_ERROR")}
      </div>
    );
  }

  if (!previewMeta) {
    return (
      <div className="tripper-rich-preview__message" role="status">
        {t("UNAVAILABLE")}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        ref={containerRef}
        className={`tripper-rich-launcher${isMobile ? " tripper-rich-launcher--narrow" : ""}`}
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        {/* ── Mobile top container (hero image + map) ───────────── */}
        {isMobile && (
          <div className="tripper-rich-launcher__inline-map">
            {nameChip}

            {/* Hero image (visible during overview, fades out for stops) */}
            <div
              className={`tripper-rich-launcher__hero${
                isOverview ? "" : " tripper-rich-launcher__hero--hidden"
              }`}
            >
              {previewMeta.heroImageUrl ? (
                <img
                  className="tripper-rich-launcher__hero-img"
                  src={previewMeta.heroImageUrl}
                  alt={previewMeta.name}
                />
              ) : (
                <div className="tripper-rich-launcher__hero-placeholder">
                  <PlaceholderIcon />
                </div>
              )}
            </div>

            {/* Mini map (fades in for stops) */}
            {boundsCenter && mapMounted && (
              <SilentErrorBoundary label="InlineMap">
                <RichLauncherMap
                  ref={mapHandleRef}
                  center={boundsCenter}
                  hidden={isOverview}
                  accentColor={accentColor}
                  mapTheme={mapTheme}
                  map3dObjects={map3dObjects}
                  map3dBuildings={map3dBuildings}
                  map3dTrees={map3dTrees}
                  showPoi={showPoi}
                  activeMarker={
                    activeStop?.coordinates
                      ? {
                          coordinates: activeStop.coordinates,
                          number: activeStop.markerNumber,
                        }
                      : null
                  }
                  onReady={handleMapReady}
                />
              </SilentErrorBoundary>
            )}
          </div>
        )}

        {/* ── Left panel ──────────────────────────────────────────── */}
        <div className="tripper-rich-launcher__left">
          {nameChip}

          {/* Floating overlay: CTA (always visible) */}
          <div className="tripper-rich-launcher__floater">
            <button
              type="button"
              className="tripper-rich-launcher__cta"
              onClick={() => {
                trackPostHogEvent("widget_launch");
                setModalOpen(true);
              }}
            >
              {t("RICH_PREVIEW_CTA")}
            </button>
          </div>

          {/* Content slides */}
          <div className="tripper-rich-launcher__slides">
            {/* Overview slide */}
            <div
              className={`tripper-rich-launcher__slide tripper-rich-launcher__slide--overview${
                isOverview
                  ? " tripper-rich-launcher__slide--active"
                  : activeIndex > 0
                    ? " tripper-rich-launcher__slide--exited"
                    : ""
              }`}
            >
              <div className="tripper-rich-launcher__overview-content">
                {previewMeta.orgName && (
                  <span className="tripper-rich-launcher__org">
                    {previewMeta.orgName}
                  </span>
                )}
                <h3 className="tripper-rich-launcher__title">
                  {previewMeta.name}
                </h3>
                <div className="tripper-rich-launcher__stats">
                  <span>
                    {t("STOP_COUNT", { count: previewMeta.stopCount })}
                  </span>
                  {previewMeta.distanceKm != null && (
                    <>
                      <span className="tripper-rich-launcher__stats-divider" />
                      <span>
                        {t("DISTANCE_KM", {
                          distance: previewMeta.distanceKm,
                        })}
                      </span>
                    </>
                  )}
                  {!previewMeta.isDayTrip && previewMeta.segmentCount > 1 && (
                    <>
                      <span className="tripper-rich-launcher__stats-divider" />
                      <span>
                        {t("DAY_COUNT", { count: previewMeta.segmentCount })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Stop slides */}
            {resolvedStops.map((rs, i) => {
              const slideIndex = i + 1;
              const isActive = activeIndex === slideIndex;
              const isExited =
                activeIndex > slideIndex ||
                (activeIndex === 0 && i === resolvedStops.length - 1);
              return (
                <div
                  key={rs.stop._id}
                  className={`tripper-rich-launcher__slide tripper-rich-launcher__slide--stop${
                    isActive
                      ? " tripper-rich-launcher__slide--active"
                      : isExited
                        ? " tripper-rich-launcher__slide--exited"
                        : ""
                  }`}
                >
                  <div className="tripper-rich-launcher__stop-image-wrapper">
                    {rs.imageUrl ? (
                      <img
                        className="tripper-rich-launcher__stop-image"
                        src={rs.imageUrl}
                        alt={rs.name}
                      />
                    ) : (
                      <div className="tripper-rich-launcher__stop-image-placeholder">
                        <PlaceholderIcon />
                      </div>
                    )}
                  </div>
                  <div className="tripper-rich-launcher__stop-text">
                    <span className="tripper-rich-launcher__stop-name" aria-label={rs.name}>
                      <SlotText text={rs.name} />
                    </span>
                    {rs.address && (
                      <span
                        className="tripper-rich-launcher__stop-address"
                        style={{ "--addr-delay": `${rs.name.length * SLOT_STAGGER_MS + 80}ms` } as React.CSSProperties}
                      >
                        {rs.address}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right panel ─────────────────────────────────────────── */}
        <div className="tripper-rich-launcher__right">
          {/* Featured photo — shown below 780px instead of the mosaic */}
          <div
            className={`tripper-rich-launcher__hero${
              isOverview ? "" : " tripper-rich-launcher__hero--hidden"
            }`}
          >
            {previewMeta.heroImageUrl ? (
              <img
                className="tripper-rich-launcher__hero-img"
                src={previewMeta.heroImageUrl}
                alt={previewMeta.name}
              />
            ) : (
              <div className="tripper-rich-launcher__hero-placeholder">
                <PlaceholderIcon />
              </div>
            )}
          </div>

          {/* Photo mosaic grid — shown at 780px and above */}
          <RichLauncherMosaic
            imageUrls={mosaicImageUrls}
            hidden={!isOverview}
          />

          {/* Mini map (isolated by error boundary) — desktop only */}
          {!isMobile && boundsCenter && mapMounted && (
            <SilentErrorBoundary label="Map">
              <RichLauncherMap
                ref={mapHandleRef}
                center={boundsCenter}
                hidden={isOverview}
                accentColor={accentColor}
                mapTheme={mapTheme}
                map3dObjects={map3dObjects}
                map3dBuildings={map3dBuildings}
                map3dTrees={map3dTrees}
                showPoi={showPoi}
                activeMarker={
                  activeStop?.coordinates
                    ? {
                        coordinates: activeStop.coordinates,
                        number: activeStop.markerNumber,
                      }
                    : null
                }
                onReady={handleMapReady}
              />
            </SilentErrorBoundary>
          )}
        </div>
      </div>

      {modalOpen && (
        <SilentErrorBoundary label="Modal" onError={() => setModalOpen(false)}>
          <TripperModal
            itineraryId={itineraryId}
            language={language}
            onClose={() => setModalOpen(false)}
          />
        </SilentErrorBoundary>
      )}
    </>
  );
};

// ── Slot-text animation helper ────────────────────────────────────────────────

const SLOT_STAGGER_MS = 20;

function SlotText({
  text,
  delayOffset = 0,
}: {
  text: string;
  delayOffset?: number;
}) {
  // Tokenize into words and whitespace runs so each word can be a single
  // wrap-unit. Without this, every char is its own flex item and lines can
  // break mid-word.
  const tokens = text.split(/(\s+)/);
  let charIndex = 0;
  return (
    <>
      {tokens.map((tok, ti) => {
        if (tok === "") return null;
        if (/^\s+$/.test(tok)) {
          charIndex += tok.length;
          return (
            <span key={ti} aria-hidden>
              {tok.replace(/\s/g, "\u00A0")}
            </span>
          );
        }
        const startIdx = charIndex;
        charIndex += tok.length;
        return (
          <span key={ti} className="tripper-slot-word">
            {[...tok].map((ch, ci) => (
              <span key={ci} className="tripper-slot-char" aria-hidden>
                <span
                  className="tripper-slot-char__inner"
                  style={{ "--d": `${delayOffset + (startIdx + ci) * SLOT_STAGGER_MS}ms` } as React.CSSProperties}
                >
                  {ch}
                </span>
              </span>
            ))}
          </span>
        );
      })}
    </>
  );
}

// ── Inline SVGs ──────────────────────────────────────────────────────────────

const PlaceholderIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
    <line x1="9" y1="3" x2="9" y2="18" />
    <line x1="15" y1="6" x2="15" y2="21" />
  </svg>
);
